import {
  Camera,
  CameraOff,
  ChevronRight,
  Mic,
  RotateCcw,
  Sparkles,
  Square,
  Upload
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";
import { toast } from "sonner";

import type {
  WelcomeVideoTrim,
  WelcomeVideoUploadMeta
} from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { ASSISTANT_NAME } from "../../model";

import {
  useWelcomeRecorder,
  type RecordedTake,
  type RecorderPhase
} from "./useWelcomeRecorder";
import {
  CLIENT_CROP_ASPECT,
  COUNTDOWN_SECONDS,
  MAX_RECORDING_SECONDS,
  TARGET_SECONDS,
  draftTalkingPoints,
  formatClock,
  lineAt,
  linesOf,
  speakingSeconds,
  type TalkingPointsInput
} from "./welcomeVideo";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientFirstName: string;
  /** What the welcome screen shows beside the video. */
  title: string;
  note: string;
  talkingPoints: TalkingPointsInput;
  onUse: (file: File, meta: WelcomeVideoUploadMeta) => void;
  onUploadInstead: () => void;
  /** A template's video plays on every intake made from it, for any client. */
  mode?: "intake" | "template";
  /** Casey drafts the talking points; empty when it couldn't. */
  onAskCasey?: () => Promise<string[]>;
};

/** The take's trim, or null when it keeps the whole thing. */
export const takeTrim = (
  [start, end]: number[],
  seconds: number
): WelcomeVideoTrim | null => {
  const from = start > 0.05 ? Math.round(start * 10) / 10 : 0;
  const to = end < seconds - 0.05 ? Math.round(end * 10) / 10 : null;
  return from || to ? { start: from, end: to } : null;
};

/** Share of the 16:9 frame that the client's video panel keeps. */
const CROP_WIDTH = (9 / 16) * CLIENT_CROP_ASPECT * 100;

const STEPS = ["Set Up", "Record", "Review"];

const stepOf = (phase: RecorderPhase): number =>
  phase === "recorded"
    ? 2
    : phase === "recording" || phase === "countdown"
    ? 1
    : 0;

const LiveVideo: FC<{ stream: MediaStream | null }> = ({ stream }) => {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.srcObject = stream;
  }, [stream]);
  return (
    <video
      ref={ref}
      autoPlay
      muted
      playsInline
      className="size-full -scale-x-100 object-cover"
    />
  );
};

const CropGuide: FC<{ label: string }> = ({ label }) => {
  const side = `${(100 - CROP_WIDTH) / 2}%`;
  return (
    <div className="pointer-events-none absolute inset-0 flex">
      <div className="h-full bg-black/60" style={{ width: side }} />
      <div
        className="h-full rounded-md ring-1 ring-white/70"
        style={{ width: `${CROP_WIDTH}%` }}
      />
      <div className="flex h-full flex-1 items-end bg-black/60 p-3">
        <span className="text-sm text-white/80">{label}</span>
      </div>
    </div>
  );
};

const CameraProblem: FC<{
  blocked: boolean;
  onRetry: () => void;
  onUpload: () => void;
}> = ({ blocked, onRetry, onUpload }) => (
  <div className="flex size-full flex-col items-center justify-center gap-2 bg-fill-2 p-6 text-center">
    <CameraOff className="size-5 text-fg-3" />
    <span className="text-base font-medium text-fg">
      {blocked ? "Camera access is blocked" : "No camera to record with"}
    </span>
    <p className="max-w-sm text-sm text-fg-3">
      {blocked
        ? "Allow the camera and microphone for this site from the icon in your browser's address bar, then try again."
        : "We couldn't find a camera, or this browser can't record video. You can upload a video instead."}
    </p>
    <div className="mt-1 flex items-center gap-2">
      {blocked ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try Again
        </Button>
      ) : null}
      <Button variant="ghost" size="sm" onClick={onUpload}>
        <Upload /> Upload a File Instead
      </Button>
    </div>
  </div>
);

/** The take inside a replica of the client's welcome screen, trimmed. */
const ClientPreview: FC<{
  take: RecordedTake;
  title: string;
  note: string;
  range: number[];
  /** The handle just moved: show that frame. */
  seek: number | null;
}> = ({ take, title, note, range, seek }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [start, end] = range;
  useEffect(() => {
    if (seek !== null && ref.current) {
      ref.current.pause();
      ref.current.currentTime = seek;
    }
  }, [seek]);
  return (
    <div className="flex size-full bg-surface">
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2 p-6">
        <span className="text-xl font-medium text-fg">{title || "Intake"}</span>
        {note ? (
          <p className="line-clamp-4 text-base text-fg-2">{note}</p>
        ) : null}
        <span className="mt-2 inline-flex h-7 w-fit items-center rounded-md bg-primary px-3 text-base text-primary-foreground">
          Start
        </span>
      </div>
      <div
        className="h-full bg-black"
        style={{ aspectRatio: CLIENT_CROP_ASPECT }}
      >
        <video
          ref={ref}
          src={take.url}
          controls
          autoPlay
          playsInline
          className="size-full object-cover"
          onPlay={(e) => {
            const v = e.currentTarget;
            if (v.currentTime < start || v.currentTime >= end - 0.05)
              v.currentTime = start;
          }}
          onTimeUpdate={(e) => {
            const v = e.currentTarget;
            if (!v.paused && v.currentTime >= end) {
              v.pause();
              v.currentTime = start;
            }
          }}
        >
          <track kind="captions" />
        </video>
      </div>
    </div>
  );
};

const TrimBar: FC<{
  seconds: number;
  range: number[];
  onChange: (range: number[], moved: number) => void;
}> = ({ seconds, range, onChange }) => {
  const [start, end] = range;
  return (
    <div className="flex flex-col gap-1.5" data-testid="welcome-trim">
      <div className="flex items-center gap-2 text-sm">
        <span className="text-fg-2">Trim</span>
        <span className="text-fg-3 tabular-nums">
          {formatClock(start)} to {formatClock(end)}
        </span>
        <span className="flex-1" />
        <span className="text-fg tabular-nums">{formatClock(end - start)}</span>
      </div>
      <Slider
        min={0}
        max={seconds}
        step={0.1}
        minStepsBetweenThumbs={10}
        value={range}
        onValueChange={(next) =>
          onChange(next, next[0] !== start ? next[0] : next[1])
        }
        thumbProps={{ "aria-label": "Trim" }}
      />
      <span className="text-sm text-fg-3">
        Drag the ends to skip a slow start or a long goodbye. The client sees
        only this part.
      </span>
    </div>
  );
};

const TakeDetails: FC<{
  take: RecordedTake;
  client: string;
  template: boolean;
  length: number;
  saveToMine: boolean;
  onSaveToMine: (save: boolean) => void;
}> = ({ take, client, template, length, saveToMine, onSaveToMine }) => (
  <>
    <span className="text-base font-medium text-fg">As {client} Sees It</span>
    <p className="text-sm text-fg-3">
      The welcome screen on a computer. The camera was mirrored while you
      recorded; {client} sees it the right way round.
    </p>
    <dl className="flex flex-col divide-y divide-line-1 text-base">
      <div className="flex items-center justify-between py-2">
        <dt className="text-fg-3">Length</dt>
        <dd className="text-fg tabular-nums">{formatClock(length)}</dd>
      </div>
      <div className="flex items-center justify-between py-2">
        <dt className="text-fg-3">Sound</dt>
        <dd className={take.quiet ? "text-warn" : "text-success"}>
          {take.quiet ? "Quiet" : "Clear"}
        </dd>
      </div>
      <div className="flex items-center justify-between py-2">
        <dt className="text-fg-3">Shown On</dt>
        <dd className="text-fg">
          {template
            ? "Every new intake from this template"
            : "This intake only"}
        </dd>
      </div>
    </dl>
    {template ? (
      <div className="flex flex-col gap-0.5 rounded-lg bg-fill-1 p-3">
        <span className="text-sm font-medium text-fg">
          Shared With the Firm
        </span>
        <span className="text-sm text-fg-3">
          It goes in Firm Videos, so anyone at the firm can use it on their
          templates.
        </span>
      </div>
    ) : (
      <div className="flex items-start gap-2 rounded-lg bg-fill-1 p-3">
        <Checkbox
          id="welcome-save-to-mine"
          checked={saveToMine}
          onCheckedChange={(v) => onSaveToMine(v === true)}
          className="mt-0.5"
        />
        <span className="flex flex-col gap-0.5">
          <Label
            htmlFor="welcome-save-to-mine"
            className="text-sm font-medium text-fg"
          >
            Save to My Videos
          </Label>
          <span className="text-sm text-fg-3">
            Use it again on another intake from Use a Previous Video.
          </span>
        </span>
      </div>
    )}
    {take.quiet ? (
      <p className="text-sm text-fg-3">
        We barely heard you. Check the microphone, or record again a little
        closer to it.
      </p>
    ) : null}
  </>
);

const DeviceSelect: FC<{
  icon: ReactNode;
  placeholder: string;
  value: string;
  devices: MediaDeviceInfo[];
  onChange: (id: string) => void;
  meter?: ReactNode;
}> = ({ icon, placeholder, value, devices, onChange, meter }) => (
  <Select
    value={value || undefined}
    onValueChange={onChange}
    disabled={!devices.length}
  >
    <SelectTrigger className="h-8 min-w-0 flex-1" aria-label={placeholder}>
      <span className="text-fg-3 [&_svg]:size-3.5">{icon}</span>
      <SelectValue placeholder={placeholder} />
      {meter}
    </SelectTrigger>
    <SelectContent>
      {devices.map((d, i) => (
        <SelectItem key={d.deviceId} value={d.deviceId}>
          {d.label || `${placeholder} ${i + 1}`}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const LevelMeter: FC<{ level: number }> = ({ level }) => {
  const lit = Math.min(5, Math.ceil(level * 12));
  return (
    <span className="ml-auto flex h-3 items-end gap-0.5" aria-hidden>
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={cn(
            "w-1 rounded-full",
            i < lit ? "bg-success" : "bg-fill-3"
          )}
          style={{ height: `${40 + i * 15}%` }}
        />
      ))}
    </span>
  );
};

type BodyProps = Omit<Props, "open" | "onOpenChange"> & {
  onBusy: (busy: boolean) => void;
  onClose: () => void;
};

/** Mounted only while the dialog is open, so the camera goes with it. */
const RecorderBody: FC<BodyProps> = ({
  clientFirstName,
  title,
  note,
  talkingPoints,
  onUse,
  onUploadInstead,
  mode = "intake",
  onAskCasey,
  onBusy,
  onClose
}) => {
  const template = mode === "template";
  const rec = useWelcomeRecorder(true);
  const [range, setRange] = useState<number[]>([0, 0]);
  const [seek, setSeek] = useState<number | null>(null);
  const [saveToMine, setSaveToMine] = useState(false);
  const [asking, setAsking] = useState(false);
  const takeSeconds = rec.take?.seconds ?? 0;
  const drafted = useMemo(
    () => draftTalkingPoints(talkingPoints).join("\n"),
    [talkingPoints]
  );
  const [script, setScript] = useState(drafted);
  const [skipped, setSkipped] = useState(0);
  const lines = linesOf(script);
  const live = rec.phase === "recording" || rec.phase === "countdown";
  const step = stepOf(rec.phase);
  const name = (!template && clientFirstName) || "The client";
  const nameInSentence = (!template && clientFirstName) || "the client";

  useEffect(() => {
    onBusy(live);
  }, [live, onBusy]);

  useEffect(() => {
    if (rec.phase === "countdown") setSkipped(0);
  }, [rec.phase]);

  useEffect(() => {
    setRange([0, takeSeconds]);
    setSeek(null);
  }, [takeSeconds]);

  const askCasey = async () => {
    if (!onAskCasey) return;
    setAsking(true);
    try {
      const next = await onAskCasey();
      if (next.length) setScript(next.join("\n"));
      else
        toast.message(
          `${ASSISTANT_NAME} couldn't draft them this time. Yours are unchanged.`
        );
    } catch {
      toast.error(
        `${ASSISTANT_NAME} couldn't draft them. Yours are unchanged.`
      );
    } finally {
      setAsking(false);
    }
  };

  const current =
    rec.phase === "recording"
      ? Math.min(lines.length - 1, lineAt(lines, rec.elapsed) + skipped)
      : 0;

  const uploadInstead = () => {
    onClose();
    onUploadInstead();
  };

  return (
    <>
      <div className="flex items-center gap-4 border-b border-line-2 py-3 pr-12 pl-4">
        <DialogTitle className="text-md font-medium">
          {template
            ? "Welcome Video for This Template"
            : `Welcome Video for ${name}`}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Record a short video {nameInSentence} sees on the first screen of the
          intake.
        </DialogDescription>
        <ol className="ml-auto flex items-center gap-1.5 text-sm">
          {STEPS.map((label, i) => (
            <li key={label} className="flex items-center gap-1.5">
              {i > 0 ? <ChevronRight className="size-3 text-fg-4" /> : null}
              <span
                className={cn(
                  i === step ? "text-fg" : i < step ? "text-fg-3" : "text-fg-4"
                )}
              >
                {label}
              </span>
            </li>
          ))}
        </ol>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex flex-col gap-3 p-4">
          <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black">
            {rec.phase === "recorded" && rec.take ? (
              <ClientPreview
                take={rec.take}
                title={title}
                note={note}
                range={range}
                seek={seek}
              />
            ) : rec.phase === "blocked" || rec.phase === "unavailable" ? (
              <CameraProblem
                blocked={rec.phase === "blocked"}
                onRetry={rec.retry}
                onUpload={uploadInstead}
              />
            ) : (
              <>
                <LiveVideo stream={rec.stream} />
                {rec.phase === "starting" ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white">
                    <Spinner />
                    <span className="text-sm text-white/70">
                      Allow camera and microphone access to start.
                    </span>
                  </div>
                ) : (
                  <CropGuide label={`${name} sees what's inside the box`} />
                )}
                {rec.phase === "recording" && lines.length ? (
                  <button
                    type="button"
                    onClick={() => setSkipped((n) => n + 1)}
                    className="absolute inset-x-0 top-3 mx-auto flex w-3/5 flex-col items-center gap-1 rounded-lg bg-black/60 px-3 py-2 text-center"
                  >
                    <span className="text-md text-white">{lines[current]}</span>
                    {lines[current + 1] ? (
                      <span className="text-sm text-white/60">
                        {lines[current + 1]}
                      </span>
                    ) : null}
                  </button>
                ) : null}
                {rec.phase === "ready" ? (
                  <div className="absolute inset-x-0 top-3 mx-auto w-fit rounded-md bg-black/60 px-2 py-1 text-sm text-white/80">
                    Your talking points scroll here, just under the camera.
                  </div>
                ) : null}
                {rec.phase === "countdown" ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="flex size-20 items-center justify-center rounded-full bg-black/60 text-2xl text-white tabular-nums">
                      {rec.countdown}
                    </span>
                  </div>
                ) : null}
                {rec.phase === "recording" ? (
                  <span className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-black/60 px-2 py-0.5 text-sm text-white tabular-nums">
                    <span className="size-2 animate-pulse rounded-full bg-danger" />
                    {formatClock(rec.elapsed)}
                  </span>
                ) : null}
              </>
            )}
          </div>

          {rec.phase === "recorded" && rec.take ? (
            <TrimBar
              seconds={rec.take.seconds}
              range={range}
              onChange={(next, moved) => {
                setRange(next);
                setSeek(moved);
              }}
            />
          ) : rec.phase === "ready" || rec.phase === "starting" ? (
            <div className="flex items-center gap-2">
              <DeviceSelect
                icon={<Camera />}
                placeholder="Camera"
                value={rec.cameraId}
                devices={rec.cameras}
                onChange={rec.chooseCamera}
              />
              <DeviceSelect
                icon={<Mic />}
                placeholder="Microphone"
                value={rec.micId}
                devices={rec.mics}
                onChange={rec.chooseMic}
                meter={<LevelMeter level={rec.level} />}
              />
              <Button
                variant="primary"
                size="md"
                disabled={rec.phase !== "ready"}
                onClick={rec.start}
              >
                <span className="size-2 rounded-full bg-danger" />
                Start Recording
              </Button>
            </div>
          ) : live ? (
            <div className="flex flex-col gap-2">
              <div className="relative">
                <Progress
                  value={(rec.elapsed / MAX_RECORDING_SECONDS) * 100}
                  aria-label="Recording length"
                />
                <span
                  className="absolute top-1/2 h-2.5 w-px -translate-y-1/2 bg-fg-3"
                  style={{
                    left: `${(TARGET_SECONDS / MAX_RECORDING_SECONDS) * 100}%`
                  }}
                />
              </div>
              <div className="flex items-center gap-2 text-sm text-fg-3">
                <span className="text-fg tabular-nums">
                  {formatClock(rec.elapsed)}
                </span>
                <span>
                  {rec.elapsed < TARGET_SECONDS
                    ? "Aim for under a minute."
                    : "Past a minute. Wrap up when you can."}{" "}
                  Stops at {formatClock(MAX_RECORDING_SECONDS)}.
                </span>
                <span className="flex-1" />
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={rec.phase !== "recording"}
                  onClick={rec.startOver}
                >
                  <RotateCcw /> Start Over
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={rec.phase !== "recording"}
                  onClick={rec.stop}
                >
                  <Square /> Stop
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex min-h-0 flex-col gap-3 border-l border-line-2 p-4">
          {rec.phase === "recorded" && rec.take ? (
            <TakeDetails
              take={rec.take}
              client={name}
              template={template}
              length={range[1] - range[0]}
              saveToMine={saveToMine}
              onSaveToMine={setSaveToMine}
            />
          ) : (
            <>
              <div className="flex items-center justify-between">
                <span className="text-base font-medium text-fg">
                  Talking Points
                </span>
                {live ? null : (
                  <span className="flex items-center gap-1">
                    {script !== drafted ? (
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => setScript(drafted)}
                      >
                        <RotateCcw /> Reset
                      </Button>
                    ) : null}
                    {onAskCasey ? (
                      <Button
                        variant="ghost"
                        size="xs"
                        disabled={asking}
                        onClick={() => void askCasey()}
                        data-testid="talking-points-redraft"
                      >
                        {asking ? <Spinner className="size-3" /> : <Sparkles />}
                        Redraft With {ASSISTANT_NAME}
                      </Button>
                    ) : null}
                  </span>
                )}
              </div>
              <p className="text-sm text-fg-3">
                {template
                  ? "Drafted from the template, with no name or dates so it fits every client"
                  : "Drafted from the intake"}
                , about {speakingSeconds(lines)} seconds out loud. They scroll
                above the camera while you record, so you look near the lens.
                Click them to skip ahead.
              </p>
              {live ? (
                <ol className="flex flex-col gap-2 text-md">
                  {lines.map((line, i) => (
                    <li
                      key={`${i}-${line}`}
                      className={cn(
                        i === current && rec.phase === "recording"
                          ? "text-fg"
                          : "text-fg-4"
                      )}
                    >
                      {line}
                    </li>
                  ))}
                </ol>
              ) : (
                <Textarea
                  value={script}
                  onChange={(e) => setScript(e.target.value)}
                  rows={11}
                  aria-label="Talking points, one per line"
                  className="resize-none text-md"
                />
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 border-t border-line-2 px-4 py-3">
        {rec.phase === "recorded" && rec.take ? (
          <>
            <Button variant="secondary" size="md" onClick={rec.retake}>
              <RotateCcw /> Record Again
            </Button>
            <span className="flex-1" />
            <Button
              variant="primary"
              size="md"
              onClick={() => {
                const { take } = rec;
                if (!take) return;
                onUse(
                  new File([take.blob], take.fileName, {
                    type: take.blob.type
                  }),
                  {
                    durationSeconds: take.seconds,
                    trim: takeTrim(range, take.seconds),
                    saveToMyVideos: !template && saveToMine
                  }
                );
                onClose();
              }}
            >
              Use This Video
            </Button>
          </>
        ) : live ? (
          <span className="text-sm text-fg-3">
            Nothing is saved or sent until you choose Use This Video.
          </span>
        ) : (
          <>
            <Button variant="ghost" size="md" onClick={uploadInstead}>
              <Upload /> Upload a File Instead
            </Button>
            <span className="flex-1" />
            {rec.phase === "ready" ? (
              <span className="text-sm text-fg-3">
                A {COUNTDOWN_SECONDS}-second countdown starts when you press
                Start Recording.
              </span>
            ) : null}
          </>
        )}
      </div>
    </>
  );
};

export const RecordWelcomeVideoDialog: FC<Props> = ({
  open,
  onOpenChange,
  ...rest
}) => {
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="gap-0 overflow-hidden p-0 sm:max-w-5xl"
        onInteractOutside={(e) => busy && e.preventDefault()}
        onEscapeKeyDown={(e) => busy && e.preventDefault()}
      >
        <RecorderBody
          {...rest}
          onBusy={setBusy}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
};
